/**
 * O painel do caso no topo da mesa do perito (E78.F4).
 *
 * Antes, a mesa era uma pilha de N cópias do quadro inteiro: com as cinco peças
 * do caso real o perito rolava cinco telas para achar as duas que divergiram, e
 * nada dizia o que estava sendo comparado nem quanto faltava para o laudo.
 *
 * Aqui isso cabe em três linhas: a frase do que o caso confronta (N peças
 * recebidas contra UM protocolo), a tabela das peças com a régua de cada uma, e
 * o contador do que falta decidir. A linha clicada seleciona a peça — a
 * navegação da mesa é esta, e não abas (decisão do owner, 2026-09-01).
 */
import {
  Badge,
  Blueprint,
  Button,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  ExameEletronicoCcbData,
  GrauConvergencia,
  ProtocoloAssinaturaCasoData,
} from '@/lib/exame_eletronico';
import { alertasDaPeca, tituloDaPeca } from '@/lib/exame_eletronico_peca';
import { fraseDoConfronto } from '@/lib/exame_eletronico_resumo';
import type { Tendencia } from '@/lib/pre_analise';
import { rotuloDaTendencia, vereditoDaTendencia, VEREDITO_TOM } from '@/lib/veredito';

import { GRAU_VARIANT } from './QuadroVerificacaoTabelas';

const t = ptBR.mesaEletronica;
const tq = ptBR.exameEletronico.quadro;

/** Os 64 caracteres do código não cabem na coluna; o meio some, as pontas ficam. */
function abreviado(valor: string): string {
  return valor.length <= 20 ? valor : `${valor.slice(0, 8)}…${valor.slice(-6)}`;
}

function CelulaGrau({ grau }: { grau: GrauConvergencia | null }): JSX.Element {
  if (grau === null) return <>—</>;
  return (
    <Badge variant={GRAU_VARIANT[grau]} data-grau={grau}>
      {tq.grauOpcoes[grau]}
    </Badge>
  );
}

function CelulaTendencia({ tendencia }: { tendencia: Tendencia | null }): JSX.Element {
  if (tendencia === null) return <>—</>;
  return (
    <Badge variant={VEREDITO_TOM[vereditoDaTendencia(tendencia)]} data-tendencia={tendencia}>
      {rotuloDaTendencia(tendencia)}
    </Badge>
  );
}

function CelulaCodigo({ sha256 }: { sha256: string | null }): JSX.Element {
  if (sha256 === null) return <>—</>;
  return <MonoData title={sha256}>{abreviado(sha256)}</MonoData>;
}

function LinhaDaPeca({
  peca,
  indice,
  selecionada,
  onSelecionar,
}: {
  peca: ExameEletronicoCcbData;
  indice: number;
  selecionada: boolean;
  onSelecionar: () => void;
}): JSX.Element {
  const titulo = tituloDaPeca(peca, indice);
  const concluida = peca.exame?.status === 'concluido';
  const grau = peca.exame?.grau_convergencia ?? peca.exame?.resultado?.grau_convergencia ?? null;
  const tendencia = peca.exame?.tendencia ?? null;
  const alertas = alertasDaPeca(peca);
  return (
    <TableRow
      className="cursor-pointer"
      data-testid="painel-peca-linha"
      data-source-document={peca.source_document_id}
      data-selecionada={selecionada}
      onClick={onSelecionar}
    >
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          className="px-0 text-left"
          onClick={onSelecionar}
          data-testid={`painel-peca-abrir-${peca.source_document_id}`}
          aria-label={t.abrirPeca.replace('{peca}', titulo)}
        >
          {titulo}
        </Button>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <CelulaCodigo sha256={peca.documento_content_sha256} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <CelulaGrau grau={grau} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <CelulaTendencia tendencia={tendencia} />
      </TableCell>
      <TableCell>{alertas ?? '—'}</TableCell>
      <TableCell>
        <Badge
          variant={concluida ? 'success' : 'neutral'}
          data-testid={`painel-peca-situacao-${peca.source_document_id}`}
          data-situacao={concluida ? 'concluida' : 'a_decidir'}
        >
          {concluida ? t.situacaoConcluida : t.situacaoADecidir}
        </Badge>
      </TableCell>
    </TableRow>
  );
}

export interface PainelDoCasoProps {
  protocolo: ProtocoloAssinaturaCasoData | null;
  pecas: readonly ExameEletronicoCcbData[];
  selecionada: string | null;
  onSelecionar: (sourceDocumentId: string) => void;
}

export function PainelDoCaso({
  protocolo,
  pecas,
  selecionada,
  onSelecionar,
}: PainelDoCasoProps): JSX.Element {
  const concluidas = pecas.filter((peca) => peca.exame?.status === 'concluido').length;
  const faltam = pecas.length - concluidas;
  const contador = t.contador
    .replace('{concluidas}', String(concluidas))
    .replace('{total}', String(pecas.length));
  const pendencia =
    faltam === 0
      ? null
      : faltam === 1
        ? t.contadorFaltaUma
        : t.contadorFaltam.replace('{n}', String(faltam));

  return (
    <Blueprint as="section" className="space-y-sc-4 p-sc-6" data-testid="painel-do-caso">
      <header>
        <h2 className="font-display text-[13px] uppercase tracking-[0.06em] text-fg-muted">
          {t.painelTitulo}
        </h2>
        <p className="mt-sc-1 max-w-prose text-body-md text-fg" data-testid="painel-confronto">
          {fraseDoConfronto(protocolo, pecas.length).join(' · ')}
        </p>
        <p className="mt-sc-2 text-body-sm text-fg-muted" data-testid="painel-contador">
          {contador}
          {pendencia === null ? '' : ` · ${pendencia}`}
        </p>
      </header>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.colunaPeca}</TableHead>
              <TableHead>{t.colunaCodigo}</TableHead>
              <TableHead>{t.colunaGrau}</TableHead>
              <TableHead>{t.colunaTendencia}</TableHead>
              <TableHead>{t.colunaAlertas}</TableHead>
              <TableHead>{t.colunaSituacao}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pecas.map((peca, indice) => (
              <LinhaDaPeca
                key={peca.source_document_id}
                peca={peca}
                indice={indice}
                selecionada={selecionada === peca.source_document_id}
                onSelecionar={() => onSelecionar(peca.source_document_id)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </Blueprint>
  );
}
